import React, { FC } from 'react'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import { Flex, Text } from '@moonpig/launchpad-components'
import {
  IconSystemCards,
  IconSystemFlowers,
  IconSystemGift,
  IconSystemSearch,
} from '@moonpig/launchpad-assets'
import { InspirationItemType } from '../types'
import { useLocaleText } from '../../locale'
import { LIST_ICON_SIZE_PX } from '../constants'

const StyledInspiration = styled.li`
  cursor: pointer;
  display: flex;
  align-items: center;
  ${s({
    bgcolor: 'colorBackground01',
    border: '1px solid',
    borderColor: 'colorBorder03',
    borderRadius: 1,
    color: 'colorTextLabel',
    display: 'flex',
    gap: 8,
    py: 4,
    pl: 4,
    pr: 6,
  })}
  &:hover,
  &:focus {
    ${s({ bgcolor: 'colorBackground02' })}
  }

  &:active {
    ${s({
      bgcolor: 'colorBackgroundSelectedState',
      borderColor: 'colorBackgroundSelectedState',
    })}
  }
`

const StyledIconWrapper = styled(Flex)`
  align-items: center;

  ${s({
    borderRadius: '8px',
    color: 'colorInteractionControl',
  })}
`

const StyledTextWrapper = styled(Flex)`
  ${s({
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: 0, md: 4 },
  })}
`
const StyledDepartmentText = styled(Text)`
  ${s({
    lineHeight: { xs: '18px', md: '24px' },
    color: 'colorInteractionControl',
    typography: { xs: 'typeBodyCaption', md: 'typeBodyText' },
  })}
`

const StyledText = styled(Text)`
  ${s({
    color: 'textLabel',
    lineHeight: '24px',
  })}
`

export type DepartmentInspirationType = {
  department: string
  searchTerm: string
  value: string
}

type DepartmentInspirationProps = {
  item: DepartmentInspirationType
  itemIndex: number
  getItemProps: (props: {
    itemIndex: number
    item: InspirationItemType
  }) => Record<string, unknown>
} & React.HTMLProps<HTMLElement>

export const DepartmentInspiration: FC<DepartmentInspirationProps> = ({
  item,
  itemIndex,
  getItemProps,
}) => {
  const t = useLocaleText()
  const capitalizedSearchTerm = `${item.searchTerm[0].toUpperCase()}${item.searchTerm?.slice(
    1,
  )}`

  const { title, icon } = mapDepartmentToContent(item.department, t)

  return (
    <StyledInspiration
      tabIndex={0}
      aria-label={`${t('search.search_for')} ${capitalizedSearchTerm}`}
      {...getItemProps({
        itemIndex,
        item: {
          ...item,
          itemType: 'inspiration',
        },
      })}
      data-search-term={item.searchTerm}
      data-item-type="inspiration"
      data-parent-department={item.department}
    >
      <StyledIconWrapper>{icon}</StyledIconWrapper>
      <StyledTextWrapper>
        <StyledText>{capitalizedSearchTerm}</StyledText>
        <StyledDepartmentText>{title}</StyledDepartmentText>
      </StyledTextWrapper>
    </StyledInspiration>
  )
}
const mapDepartmentToContent = (
  department: string,
  t: ReturnType<typeof useLocaleText>,
): { title: string; icon: React.JSX.Element } => {
  const iconProps = {
    width: LIST_ICON_SIZE_PX,
    height: LIST_ICON_SIZE_PX,
  }

  switch (department) {
    case 'ALL_CARDS':
      return {
        title: t('search.trending_topic_department_title', {
          department: 'ALL_CARDS',
        }),
        icon: <IconSystemCards {...iconProps} />,
      }

    case 'ALL_GIFTS':
      return {
        title: t('search.trending_topic_department_title', {
          department: 'ALL_GIFTS',
        }),
        icon: <IconSystemGift {...iconProps} />,
      }

    case 'ALL_FLOWERS_AND_PLANTS':
      return {
        title: t('search.trending_topic_department_title', {
          department: 'ALL_FLOWERS_AND_PLANTS',
        }),
        icon: <IconSystemFlowers {...iconProps} />,
      }

    /* istanbul ignore next */
    default:
      return {
        title: '',
        icon: <IconSystemSearch {...iconProps} />,
      }
  }
}
